import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Divider,
  Chip,
} from "@mui/material";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

export default function ContactHistoryDialog({
  open,
  onClose,
  history,
  contactNumber,
}) {
  // Helper to color sentiment chip based on backend output
  const getSentimentChip = (sentiment) => {
    if (!sentiment) return null;
    const lower = sentiment.toLowerCase();

    let color = "default";
    if (lower.includes("pos")) color = "success";
    else if (lower.includes("neg")) color = "error";
    else if (lower.includes("neu")) color = "info";

    return (
      <Chip
        label={sentiment.toUpperCase()}
        color={color}
        size="small"
        sx={{ height: 20, fontSize: "0.65rem", fontWeight: "bold" }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDTypography variant="h6">
            Contact History {contactNumber ? `(${contactNumber})` : ""}
          </MDTypography>
          <IconButton onClick={onClose} size="small">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <Divider sx={{ my: 0 }} />
      <DialogContent sx={{ p: 2 }}>
        {history && history.length > 0 ? (
          <List disablePadding>
            {history.map((record, index) => (
              <React.Fragment key={index}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    borderRadius: "8px",
                    my: 1,
                    py: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    <Icon
                      color={
                        record.direction === "inbound" ? "info" : "success"
                      }
                    >
                      {record.direction === "inbound"
                        ? "call_received"
                        : "call_made"}
                    </Icon>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.5}
                      >
                        <MDTypography
                          variant="button"
                          fontWeight="medium"
                          textTransform="capitalize"
                        >
                          {record.direction || "Call"}
                        </MDTypography>
                        {getSentimentChip(record.sentiment)}
                      </MDBox>
                    }
                    secondary={
                      <MDBox component="span" display="block">
                        <MDTypography
                          variant="body2"
                          color="text"
                          fontSize="0.875rem"
                          sx={{ mb: 1, display: "block" }}
                        >
                          {record.summary ?? "No summary recorded"}
                        </MDTypography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          component="span"
                          display="block"
                        >
                          {`Agent ID: ${record.agent}`}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          component="span"
                          display="block"
                        >
                          Time: {record.created || ""}
                        </Typography>
                      </MDBox>
                    }
                  />
                </ListItem>
                {index < history.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <MDBox py={4} textAlign="center">
            <MDTypography variant="body2" color="text">
              No previous conversation history found for this contact.
            </MDTypography>
          </MDBox>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

ContactHistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  history: PropTypes.array.isRequired,
  contactNumber: PropTypes.string,
};
